import { useMemo } from 'react';
import { useAdminPermissions } from './useAdminPermissions';
import { useConnections } from './useConnections';
import { useAllPlatformUsers } from './useAllPlatformUsers';

/**
 * Unified shape for any user that can be selected in the new-chat picker.
 * Matches the superset of Connection and PlatformUser.
 */
export interface MessagingCandidate {
  id:                  string;
  full_name:           string | null;
  username:            string | null;
  profile_picture_url: string | null;
  belt_rank:           string | null;
  academy_name:        string | null;
}

/**
 * Returns the correct candidate list for the new-chat picker based on the
 * current user's role:
 *   - Admin  → all platform users (up to 500, ordered by name)
 *   - Others → connected users only
 *
 * Components remain unaware of the admin distinction.
 */
export function useMessageableCandidates(): {
  candidates: MessagingCandidate[];
  isLoading:  boolean;
  scopeLabel: string;
} {
  const { isAdmin, isLoading: adminLoading } = useAdminPermissions();

  const { connections, isLoading: connectionsLoading } = useConnections();
  const { users,       isLoading: allUsersLoading     } = useAllPlatformUsers();

  const candidates = useMemo<MessagingCandidate[]>(() => {
    if (adminLoading) return [];
    return isAdmin ? users : connections;
  }, [isAdmin, adminLoading, users, connections]);

  const isLoading = adminLoading || (isAdmin ? allUsersLoading : connectionsLoading);

  const scopeLabel = isAdmin ? 'All Users' : 'Your Connections';

  return { candidates, isLoading, scopeLabel };
}
